import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetInventoryService } from "subModule/src/services/inventory/useInventory";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, SelectChangeEvent } from "@mui/material";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import PurchaseOrderSearchHeader from "components/Inventory/PurchaseOrderSearchHeader";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { CommonProfileService } from "subModule/src/core/services";
interface SearchFormData {
  [key: string]: string | any;
}
interface POProps {
  supplierId?: number;
}
export default function PurchaseOrderListing(props: POProps) {
  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.supplierId ? 5 : 20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["poNumber", "urgencyStatus"]);
  const [search, setSearch] = useState<any>({});
  const [downloading, setDownloading] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    poNumber: "",
    status: 0,
    urgencyStatus: 0,
    supplierId: 0,
    storeId: 0,
    startDate: null,
    endDate: null,
    storeTitle: "",
    supplierTitle: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetInventoryService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    ...(props?.supplierId && { supplierId: props.supplierId }),
  });
  const { data: supplierData, refetch: supplierRefetch } = useGetVendorService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: storeData, refetch: storeRefetch } = useGetStoreDropdownService(
    { pageNumber: dropdownPage, size: 50, title: dropdownSearchTerm },
  );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const StatusList = [
    { label: t("draft"), value: 1 },
    { label: t("approved"), value: 2 },
  ];
  const UrgencyStatusList = [
    { label: t("Low"), value: 10 },
    { label: t("Medium"), value: 20 },
    { label: t("High"), value: 30 },
  ];
  useEffect(() => {
    storeRefetch();
    supplierRefetch();
  }, []);
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.startDate, searchFormData.endDate]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.status, searchFormData.urgencyStatus]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stores")
        storeRefetch().finally(() => setSearchLoading(null));
      else supplierRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) =>
    navigate("/inventory/purchase/" + row.id, {
      state: { supplier: props?.supplierId || null },
    });
  const clearSearch = () => {
    setFilter(["poNumber", "urgencyStatus"]);
    setSearchFormData({
      poNumber: "",
      status: 0,
      urgencyStatus: 0,
      supplierId: 0,
      storeId: 0,
      startDate: null,
      endDate: null,
    });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      );
      if (selectedFilters.includes("storeId") && prevFormData.storeTitle)
        updatedFormData.storeTitle = prevFormData.storeTitle;
      if (selectedFilters.includes("supplierId") && prevFormData.supplierTitle)
        updatedFormData.supplierTitle = prevFormData.supplierTitle;
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.startDate = prevFormData.startDate;
        updatedFormData.endDate = prevFormData.endDate;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "supplierId" ||
      field === "storeId" ||
      field === "status" ||
      field === "urgencyStatus"
        ? value
          ? parseInt(value.value) || 0
          : 0
        : value;
    if (
      (field === "status" && searchFormData.status === val) ||
      (field === "urgencyStatus" && searchFormData.urgencyStatus === val)
    )
      return;
    if (field === "dateInterval") {
      setSearchFormData((prevData) => ({
        ...prevData,
        startDate: value[0],
        endDate: value[1],
      }));
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
        ...(field === "storeId" && value?.label
          ? { storeTitle: value.label }
          : {}),
        ...(field === "supplierId" && value?.label
          ? { supplierTitle: value.label }
          : {}),
      }));
    }
    if (field === "status" || field === "urgencyStatus") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    const { startDate, endDate, ...rest } = searchFormData;
    if (startDate && endDate) {
      filteredSearch.startDate = startDate;
      filteredSearch.endDate = endDate;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        startDate: null,
        endDate: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const DownloadPOs = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search, ...(props?.supplierId && { supplierId: props.supplierId }) },
      "purchaseorder/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `PurchaseOrders.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  return (
    <Box>
      {props?.supplierId ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={false}
          onClick={() => navigate("/inventory/new-purchase")}
          heading={t("Purchase Orders")}
          icon={
            <ShoppingCartOutlinedIcon
              sx={{
                fill: "#000",
                width: "30px",
                height: "30px",
                marginTop: "3px",
              }}
            />
          }
          btnText={"create"}
          btnType={"create"}
          showCreateBtn={checkPrivileges("ROLE_PURCHASEORDER_CREATE")}
        />
      )}
      <Box>
        {isFetching || loading ? (
          <Loader />
        ) : (
          <div id="mainListingTable">
            <BackendPaginationTable
              columns={[
                {
                  id: "poNumber",
                  label: "Purchase Order Number",
                  numeric: false,
                  width: "14%",
                },
                { id: "poDate", label: "Date", numeric: false, width: "10%" },
                { id: "status", label: "Status", numeric: false, width: "9%" },
                {
                  id: "urgencyStatus",
                  label: t("Priority"),
                  numeric: false,
                  width: "9%",
                },
                {
                  id: "vendorTitle",
                  label: "Supplier",
                  numeric: false,
                  width: "14%",
                },
                {
                  id: "storeTitle",
                  label: "Store",
                  numeric: false,
                  width: "12%",
                },
                // {
                //   id: "subtotalAmount",
                //   label: "Subtotal",
                //   numeric: false,
                //   width: "9%",
                // },
                // {
                //   id: "discount",
                //   label: t("Discount"),
                //   numeric: false,
                //   width: "8%",
                // },
                // {
                //   id: "taxAmount",
                //   label: t("tax"),
                //   numeric: false,
                //   width: "7%",
                // },
                {
                  id: "totalAmount",
                  label: t("Total"),
                  numeric: false,
                  width: "8%",
                },
              ]}
              data={tableData?.pages[0]?.data?.content || []}
              pageNo={pageNo}
              pageSize={pageSize}
              tableData={tableData?.pages[0]?.data || {}}
              showCheckbox={false}
              showActions={true}
              enableSorting={true}
              showHeader={true}
              showSearch={true}
              showFilter={true}
              showCreateButton={true}
              handleChangePage={handleChangePage}
              handleChangeRows={handleChangeRows}
              rowClick={true}
              showRowView={showRowView}
              loadingSearch={false}
              headerComponent={
                <>
                  <Box className="table-header-search">
                    <PurchaseOrderSearchHeader
                      searchFormData={searchFormData}
                      handleChangeFilter={handleChangeFilter}
                      handleChangeSearch={handleChangeSearch}
                      handleSubmitSearch={handleSubmitSearch}
                      clearSearch={clearSearch}
                      supplierData={supplierData?.pages[0]?.data?.content || []}
                      storeData={storeData?.pages[0]?.data?.content || []}
                      {...{ filter, search, StatusList, UrgencyStatusList }}
                      suppliersResponse={supplierData || {}}
                      storesResponse={storeData || {}}
                      handleDropdownActions={handleDropdownActions}
                      supplier={props?.supplierId}
                      {...{
                        filter,
                        search,
                        dropdownPage,
                        searchLoading,
                        dropdownSearchTerm,
                        setDropdownSearchTerm,
                        setDropdownPage,
                      }}
                    />
                  </Box>
                  {checkPrivileges("ROLE_PURCHASEORDER_EXPORT") ? (
                    <Box className="table-header-actions">
                      <Button
                        id="import-button"
                        variant="contained"
                        disableElevation
                        onClick={() => {
                          setDownloading(true);
                          DownloadPOs();
                        }}
                        className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                        sx={{
                          outline: "2px solid",
                          color: themeColors?.primary,
                          borderColor: themeColors?.primary,
                          height: 34,
                          minHeight: 34,
                        }}
                        disabled={downloading}
                      >
                        {downloading ? (
                          <CircularProgress
                            sx={{
                              color: themeColors?.primary,
                              marginRight: "8px",
                            }}
                            size={16}
                          />
                        ) : null}
                        {t("export")}
                      </Button>
                    </Box>
                  ) : null}
                </>
              }
            />
          </div>
        )}
      </Box>
    </Box>
  );
}
